// Player helpers (reset PR3): transcript cues for the follow-along panel and the captions track,
// and the local resume position. Pure functions so they test without a <video>.
import type { GrackleTranscript } from "../live/types";

/** F12: every recording can have chapters (manual, or generated once an LLM is connected) —
 * with none yet, the scrubber is one segment and the Chapters tab offers "Add chapter". */
export const CHAPTERS_EMPTY = "No chapters yet";

/** One timed transcript line. `speaker` is set only when the transcript knows it. */
export interface Cue {
  start: number;
  end: number;
  text: string;
  speaker?: string;
  /** F4: stable segment id (UUID v7) when the finalized transcript carries one. */
  id?: string;
}

/** "3:41" / "1:02:10" for a position in seconds; mono in the UI (DS: every number is mono). */
export function clock(seconds: number): string {
  const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

function vttTime(raw: string): number | null {
  const m = raw.trim().match(/^(?:(\d+):)?(\d{1,2}):(\d{2})(?:[.,](\d{1,3}))?$/);
  if (!m) return null;
  const [, h, min, s, ms] = m;
  return Number(h ?? 0) * 3600 + Number(min) * 60 + Number(s) + (ms ? Number(ms.padEnd(3, "0")) / 1000 : 0);
}

/** WebVTT -> cues. Tolerant: skips the header, NOTE/STYLE blocks and cue ids; strips tags. A
 * `<v Speaker>` voice span becomes the cue's speaker. */
export function parseVtt(vtt: string): Cue[] {
  const cues: Cue[] = [];
  const blocks = vtt.replace(/\r\n?/g, "\n").split(/\n{2,}/);
  for (const block of blocks) {
    const lines = block.split("\n").filter((l) => l.trim() !== "");
    const at = lines.findIndex((l) => l.includes("-->"));
    if (at < 0) continue;
    const [from, rest = ""] = lines[at].split("-->");
    const start = vttTime(from);
    const end = vttTime(rest.trim().split(/\s+/)[0] ?? "");
    if (start === null || end === null) continue;
    const raw = lines.slice(at + 1).join(" ");
    const voice = raw.match(/<v(?:\.[^\s>]+)?\s+([^>]+)>/);
    const text = raw.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
    if (!text) continue;
    cues.push({ start, end, text, ...(voice ? { speaker: voice[1].trim() } : {}) });
  }
  return cues;
}

function vttStamp(seconds: number): string {
  const ms = Math.max(0, Math.round(seconds * 1000));
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  const rem = ms % 1000;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(rem).padStart(3, "0")}`;
}

/** Cues -> WebVTT, for the captions track when the transcript only has timed lines. */
export function toVtt(cues: Cue[]): string {
  const body = cues
    .map((c) => `${vttStamp(c.start)} --> ${vttStamp(Math.max(c.end, c.start + 0.5))}\n${c.text}`)
    .join("\n\n");
  return `WEBVTT\n\n${body}\n`;
}

/** The transcript as timed cues. The stored VTT is preferred (batch transcription writes exact
 * cue timings); a live meeting's finalized lines are the fallback. Plain text alone has no
 * timings, so it yields no cues: the panel shows it as text, and there are no captions. */
export function cuesFromTranscript(t: GrackleTranscript | null | undefined): Cue[] {
  if (!t || t.status !== "ready") return [];
  // F4: finalized segments carry stable ids and per-recording speaker names, so they win over
  // the VTT (which has neither).
  const segs = (t.segments ?? []).filter((s) => s.text.trim());
  if (segs.length > 0) {
    return segs.map((s) => ({
      start: s.start_ms / 1000,
      end: Math.max(s.end_ms, s.start_ms) / 1000,
      text: s.text,
      ...(s.speaker_name ? { speaker: s.speaker_name } : {}),
      ...(s.id ? { id: s.id } : {}),
    }));
  }
  if (t.vtt && t.vtt.trim()) {
    const cues = parseVtt(t.vtt);
    if (cues.length > 0) return cues;
  }
  const lines = (t.lines ?? []).filter((l) => l.is_final && l.text.trim());
  if (lines.length === 0) return [];
  const multiSpeaker = new Set(lines.map((l) => l.speaker_label)).size > 1;
  return lines.map((l) => ({
    start: l.t_start_ms / 1000,
    end: Math.max(l.t_end_ms, l.t_start_ms) / 1000,
    text: l.text,
    ...(multiSpeaker ? { speaker: l.speaker_label } : {}),
  }));
}

/** The whole transcript as plain text for the clipboard: one `[3:41] Priya: text` line per
 * cue, in time order, speaker names as the panel shows them. Cues without a speaker drop the
 * "Name: " part rather than inventing one. */
export function transcriptText(cues: Cue[]): string {
  return cues
    .map((c) => `[${clock(c.start)}] ${c.speaker ? `${c.speaker}: ` : ""}${c.text.trim()}`)
    .join("\n");
}

/** One diarized speaker of a finalized transcript, for FR-M5a's rename and FR-M5b's merge
 * controls. Only speakers that are not merged into another are listed; the ones merged into
 * this speaker ride along in `merged`. */
export interface TranscriptSpeaker {
  /** Provider tag (`SPEAKER_00`) — the key `PATCH /grackles/{id}/transcript/speakers` takes. */
  tag: string;
  /** What the panel shows: the editor's name when set, else the numbered default. */
  name: string;
  /** True when an editor named this speaker (so "Reset" is worth offering). */
  custom: boolean;
  /** FR-M5b: speakers merged into this one, each with its own name (what an unmerge restores). */
  merged: { tag: string; name: string }[];
}

/** The distinct speakers of a finalized transcript, in order of first appearance. Empty
 * for a live-only transcript (no segments) or one the provider returned without speakers. */
export function transcriptSpeakers(t: GrackleTranscript | null | undefined): TranscriptSpeaker[] {
  if (!t || t.status !== "ready") return [];
  const custom = t.speaker_names ?? {};
  // FR-M5b: the server's own list knows each speaker's name even while it is merged.
  if (t.speakers && t.speakers.length > 0) {
    const out: TranscriptSpeaker[] = t.speakers
      .filter((sp) => !sp.merged_into)
      .map((sp) => ({ tag: sp.tag, name: sp.name, custom: Boolean(custom[sp.tag]), merged: [] }));
    for (const sp of t.speakers) {
      if (sp.merged_into) out.find((o) => o.tag === sp.merged_into)?.merged.push({ tag: sp.tag, name: sp.name });
    }
    return out;
  }
  const seen = new Set<string>();
  const out: TranscriptSpeaker[] = [];
  for (const s of t.segments ?? []) {
    if (!s.speaker || !s.speaker_name || seen.has(s.speaker)) continue;
    seen.add(s.speaker);
    out.push({ tag: s.speaker, name: s.speaker_name, custom: Boolean(custom[s.speaker]), merged: [] });
  }
  return out;
}

/** Index of the cue playing at `time`: the last cue that has started. -1 before the first. */
export function activeCueIndex(cues: Cue[], time: number): number {
  let lo = 0;
  let hi = cues.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (cues[mid].start <= time) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

// ---- Local resume (reset PR3). Server-side, cross-device resume is out of scope. ----------

/** Offered only past the first 10 s and before the last 5%: nobody wants to resume the intro
 * or the sign-off. */
export const RESUME_MIN_SECONDS = 10;
export const RESUME_MAX_FRACTION = 0.95;

/** One entry per recording and viewer: a signed-in user's position never leaks into another
 * account's on a shared machine. Signed-out viewers share the "anon" slot. */
export function resumeKey(recordingId: string, userId?: string | null): string {
  return `grackle.resume.v1:${recordingId}:${userId || "anon"}`;
}

export interface ResumeEntry {
  /** Position, seconds */
  t: number;
  /** Duration when saved, seconds (0 if unknown) */
  d: number;
}

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null; // Storage blocked (privacy mode, sandboxed iframe): resume just doesn't happen.
  }
}

export function readResume(key: string): ResumeEntry | null {
  try {
    const raw = storage()?.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<ResumeEntry>;
    if (typeof v.t !== "number" || !Number.isFinite(v.t)) return null;
    return { t: v.t, d: typeof v.d === "number" && Number.isFinite(v.d) ? v.d : 0 };
  } catch {
    return null;
  }
}

export function writeResume(key: string, entry: ResumeEntry): void {
  try {
    storage()?.setItem(key, JSON.stringify({ t: Math.round(entry.t * 10) / 10, d: Math.round(entry.d * 10) / 10 }));
  } catch {
    // Quota or blocked storage: losing a resume point is harmless.
  }
}

export function clearResume(key: string): void {
  try {
    storage()?.removeItem(key);
  } catch {
    // ignore
  }
}

/** The position to offer, or null. `duration` is seconds (0 when not known yet). */
export function resumeOffer(entry: ResumeEntry | null, duration: number): number | null {
  if (!entry) return null;
  const d = duration > 0 ? duration : entry.d;
  if (!(d > 0)) return null;
  if (entry.t <= RESUME_MIN_SECONDS || entry.t >= d * RESUME_MAX_FRACTION) return null;
  return entry.t;
}

export const CAPTIONS_PREF_KEY = "grackle.player.captions";

export function readCaptionsPref(): boolean {
  try {
    return storage()?.getItem(CAPTIONS_PREF_KEY) === "on";
  } catch {
    return false;
  }
}

export function writeCaptionsPref(on: boolean): void {
  try {
    storage()?.setItem(CAPTIONS_PREF_KEY, on ? "on" : "off");
  } catch {
    // ignore
  }
}
